/* eslint-disable no-nested-ternary */
/* eslint-disable react/button-has-type */
import { FC, MutableRefObject, useEffect, useState } from 'react';
import { Box, Button, Svg, useThemeValue } from '@altermaind/web/ui';

type Props = {
  startRecognition: () => void;
  stopRecognition: () => void;
  speaking: boolean;
  setEndCall: (end: boolean) => void;
  setIsCameraOn: (on: boolean) => void;
  handleVideoClick: () => void;
  isCameraOn: boolean;
  streamRef: MutableRefObject<MediaStream | null>;
  vertical?: boolean;
};

export const Controls: FC<Props> = ({
  startRecognition,
  stopRecognition,
  speaking,
  setEndCall,
  setIsCameraOn,
  handleVideoClick,
  isCameraOn,
  streamRef,
  vertical = true,
}) => {
  const [isActive, setIsActive] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const handleMicClick = () => {
    if (!isInitialized) {
      setIsInitialized(true);
      setIsActive(true);
      startRecognition();
      return;
    }

    if (isActive) {
      stopRecognition();
      setIsActive(false);
    } else {
      startRecognition();
      setIsActive(true);
    }
  };

  const handleStopAll = () => {
    stopRecognition();
    setIsActive(false);
    setIsInitialized(false);
    setEndCall(true);
    setIsCameraOn(false);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      // If you need to clear the ref, use a callback or pass a setter from parent
      // Example: if you have a setStreamRef function from parent, use it here:
      // setStreamRef(null);
      // Otherwise, just stop the tracks and leave the ref as is
    }
  };

  const gapXS = useThemeValue('space', 'xs');

  useEffect(() => {
    if (speaking) {
      stopRecognition();
    } else if (isInitialized && isActive) {
      startRecognition();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speaking]);

  useEffect(() => {
    if (isInitialized && isActive && !speaking) {
      startRecognition();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, isActive]);

  return (
    <Box
      display='flex'
      flexDirection={vertical ? 'column' : 'row'}
      justifyContent='center'
      alignItems='center'
      style={{ gap: gapXS }}
    >
      <Button
        onClick={handleMicClick}
        aria-label={isActive ? 'Disattiva microfono' : 'Attiva microfono'}
        disabled={speaking}
        minWidth={0}
        p='none'
        width='60px'
        height='57px'
        border='none'
        display='flex'
        justifyContent='center'
        alignItems='center'
        borderRadius='50%'
        backgroundColor={
          speaking ? 'grey/light/background' : isActive ? 'primary' : 'grey/dark/background'
        }
        style={{
          cursor: speaking ? 'not-allowed' : 'pointer',
          transition: 'all 0.3s ease',
        }}
      >
        <Svg name='controlMicrophone' size='s' />
      </Button>

      <Button
        onClick={handleVideoClick}
        aria-label={isCameraOn ? 'Disattiva video' : 'Attiva video'}
        disabled={speaking}
        minWidth={0}
        p='none'
        width='60px'
        height='57px'
        border='none'
        display='flex'
        justifyContent='center'
        alignItems='center'
        borderRadius='50%'
        backgroundColor={
          speaking ? 'grey/light/background' : isCameraOn ? 'primary' : 'grey/dark/background'
        }
        style={{
          cursor: speaking ? 'not-allowed' : 'pointer',
          transition: 'all 0.3s ease',
        }}
      >
        <Svg name='controlCamera' size='s' />
      </Button>

      <Button
        onClick={handleStopAll}
        aria-label='Ferma riconoscimento'
        disabled={speaking}
        minWidth={0}
        p='none'
        width='60px'
        height='57px'
        border='none'
        display='flex'
        justifyContent='center'
        alignItems='center'
        borderRadius='50%'
        backgroundColor='error/bright'
      >
        <Svg name='controlClose' size='s' />
      </Button>
    </Box>
  );
};
